import React, {
  useEffect,
  useState,
} from "react";
import {
  Table as TableAntd,
  Button,
  Space,
  Tooltip,
  Select,
  Input,
  Modal,
  Form,
  Alert,
  Menu,
  Dropdown
} from "antd";
import {
  EditOutlined,
  EyeOutlined,
  LogoutOutlined,
  RetweetOutlined,
  MoreOutlined,
} from "@ant-design/icons";

import CarePlanList from "../care_plans/CarePlanList";
import SurgicalInterventionForm from "./SurgicalInterventionForm";
import SurgicalInterventionRescheduleForm from "./SurgicalInterventionRescheduleForm";
import SurgicalInterventionOutputTypeForm from "./SurgicalInterventionOutputTypeForm";
import TransferIntervention from "../transfer_intervention/TransferIntervention";

import {
  getOne as getSurgicalIntervention,
  cancel as cancelSurgicalIntervention,
  reschedule as rescheduleSurgicalIntervention,
  updateOutputType as updateOutputTypeIntervention
} from "../../api/SurgicalIntervention";
import { all as AllCarePlan } from "../../api/CarePlan";

import { cancellationReasons } from "../../constants";
import { formatFirstname, formatLastname } from '../../utils';

import moment from "moment";

const layoutForm = {
  labelCol: {
    span: 7,
  },
  wrapperCol: {
    span: 16,
    className: "mx-3",
  },
};

const { Option } = Select;

const outputTypes = {
  home: "Domicile",
  convalescence: "Convalescence",
  had: "H.A.D.",
};

/**
 *
 * @param {*} props
 * @returns
 */
const CancellationModal = (props) => {
  const { surgicalIntervention } = props
  const [form] = Form.useForm();
  const [carePlans, setCarePlans] = useState([]);

  const onOk = () => {
    form.validateFields().then(async (data) => {
      await cancelSurgicalIntervention(surgicalIntervention.id, {
        'reason_id': data.reason_id
      });
      props.onOk();
    });
  };

  const onCancel = () => {
    props.onCancel();
  };

  const resetForm = () => {
    form.resetFields();
  }

  const fetchCarePlans = async function (surgicalInterventionId) {
    AllCarePlan({
      current: 1,
      pageSize: 100,
      sort: "action_time,1",
      q: {
        "surgical_intervention_id": surgicalInterventionId,
      },
    }).then((data) => {
      setCarePlans(data.results);
    });
  };

  let cancellationReasonsOptions = cancellationReasons.map((option, index) => (
    <Option key={index} value={option.id}>
      {option.label}
    </Option>
  ));

  useEffect(() => {
    resetForm();

    if (surgicalIntervention) {
      fetchCarePlans(surgicalIntervention.id);

      const patient = surgicalIntervention.patient;
      let patientName = (patient) ? [
        formatFirstname(patient.firstname),
        formatLastname(patient.lastname),
      ].join(' ') : null;

      form.setFieldsValue({
        "patient": patientName,
        "reason_id": 1,
      });
    }
  }, [props.visible]);

  let modalContent = (
      <Modal
          {...props}
          title="Confirmez-vous l'annulation du séjour ?"
          onOk={onOk}
          onCancel={onCancel}
          cancelText={"Annuler"}
          okText={"Confirmer"}
      >
        <Alert
            message="Attention, cette action est irréversible."
            type="warning"
            className="mb-3"
        />
        <Form form={form} {...layoutForm}>
          <Form.Item
              name="patient"
              label="Nom du patient"
          >
            <Input className="ant-input-text" disabled={true} />
          </Form.Item>
          <Form.Item
              name="reason_id"
              label="Raison"
              rules={[{ required: true }]}
          >
            <Select>
              {cancellationReasonsOptions}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
  );

  if (carePlans.length > 0) {
    modalContent = (
      <Modal {...props} title="Confirmez-vous l'annulation du séjour ?"
      footer={[
        <Button key="back" type="primary" onClick={onCancel}>
          Fermer
        </Button>
      ]}>
        <Alert message="Des soins ont déjà été saisis pour ce séjour. L'annulation est impossible." type="warning" />
      </Modal>
    )
  }

  return (
    <div>
      {modalContent}
    </div>
  );
};

/**
 *
 * @param {*} props
 */
const Table = (props) => {
  const { mode, surgicalInterventions, pagination, sorter, loading, transferedSurgicalInterventionIds=[] } = props;

  const [showForm, setShowForm] = useState(false);
  const [showCarePlans, setShowCarePlans] = useState(false);
  const [showReshedule, setShowReshedule] = useState(false);
  const [showOutputType, setShowOutputType] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);

  const [selectedIntervention, setSelectedIntervention] = useState(null);

  const columns = [
    {
      title: "Patient",
      dataIndex: "patient",
      columnKey: "patient.lastname",
      sorter: true,
      render: (data) => {
        if (!data) {
          return "";
        }
        return (
          <span>
            {formatFirstname(data.firstname)} {formatLastname(data.lastname)}
          </span>
        );
      },
    },
    {
      title: "Unité",
      dataIndex: "care_unit",
      columnKey: "care_unit.name",
      sorter: true,
      render: (data) => {
        if (!data) {
          return "";
        }
        return data.name;
      },
    },
    {
      title: "Chirurgien",
      dataIndex: "doctor",
      columnKey: "doctor.firstname",
      sorter: true,
      render: (data) => {
        if (!data) {
          return "";
        }
        return (
          <span>
            {formatFirstname(data.firstname)} {formatLastname(data.lastname)}
          </span>
        );
      },
    },
    {
      title: "Procédure",
      dataIndex: "surgical_procedure",
      columnKey: "surgical_procedure.name",
      sorter: true,
      render: (data) => {
        if (!data) {
          return "";
        }
        return (
          <Tooltip title={data.name}>
            <span className="intervention-procedure-name">{data.name}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "SIIPS",
      dataIndex: "total_expected_siips",
      sorter: true,
    },
    {
      title: "Mode de sortie",
      sorter: true,
      dataIndex: "output_type",
      render: (data) => {
        if (!outputTypes.hasOwnProperty(data)) {
          return "";
        }
        return outputTypes[data];
      },
    },
    {
      title: "Date d'entrée",
      dataIndex: "start_at",
      sorter: true,
      render: (data) => {
        if (!data) {
          return "";
        }

        return moment(data).format("DD/MM/YYYY");
      },
    }
  ];

  if (mode === 'out') {
    columns.unshift({
      width: 35,
      align: "center",
      sorter: false,
      render: (data) => {
        if (!transferedSurgicalInterventionIds) {
          return "";
        }

        return (
          <span>
            {transferedSurgicalInterventionIds.indexOf(data.id) > -1 ? <RetweetOutlined /> : null}
          </span>
        );
      },
    });

    columns.push({
      title: "Date de sortie envisagée",
      dataIndex: "expected_end_at",
      sorter: true,
      render: (data) => {
        if (!data) {
          return "";
        }
        return moment(data).format("DD/MM/YYYY");
      },
    });
  }

  columns.push({
    render: (data) => {
      const patientOut = data.end_at;
      const titlePatientOut = "Patient sorti";
      const titleButtonOut = (patientOut) ? titlePatientOut : "Sortir le patient";
      const titleButtonActions = patientOut ? titlePatientOut : null;

      let buttons = [
        <Tooltip
            placement="bottom"
            title="Voir les jours d'affectation des soins"
            key="show_care_plan"
        >
          <Button
              type="primary"
              shape="circle"
              icon={<EyeOutlined />}
              size="large"
              onClick={() => onShowCarePlansHandler(data.id)}
          />
        </Tooltip>,
        <Tooltip placement="bottom" title="Editer" key="edit">
          <Button
            type="primary"
            shape="circle"
            icon={<EditOutlined />}
            size="large"
            onClick={() => onShowForm(data.id)}
          />
        </Tooltip>,
    ];

      if (mode === "out") {
        buttons.push(
          <Tooltip placement="bottom" title={titleButtonOut} key="out">
            <Button
              type="primary"
              shape="circle"
              icon={<LogoutOutlined />}
              size="large"
              onClick={() => props.onOut(data.id)}
              disabled={patientOut}
            />
          </Tooltip>
        );
      }

      const menu = (
        <Menu>
          <Menu.Item
            key="transfert"
            className="m-1 px-3 py-2"
            onClick={() => onShowTransfertHandler(data.id)}
            disabled={patientOut}
          >
            Voir les transferts
            </Menu.Item>
          <Menu.Item
            key="update_dates"
            className="m-1 px-3 py-2"
            onClick={() => onShowRescheduleHandler(data.id)}
            disabled={patientOut}
          >
            Modifier les dates
          </Menu.Item>
          <Menu.Item
            key="update_output_type"
            className="m-1 px-3 py-2"
            onClick={() => onShowOutputTypeHandler(data.id)}
            disabled={patientOut}
          >
            Modifier le mode de sortie
          </Menu.Item>
          <Menu.Item
            key="cancallation"
            className="m-1 px-3 py-2"
            onClick={() => onShowCancelInterventionModal(data.id)}
            disabled={patientOut}
          >
            Annuler le séjour
          </Menu.Item>
        </Menu>
      );
      buttons.push(
        <Tooltip placement="top" title={titleButtonActions} key="out">
          <Dropdown
            overlay={menu}
            trigger={['click']}
            placement="bottomRight"
            arrow={true}
          >
            <Button shape="circle" size="large" icon={<MoreOutlined />} />
          </Dropdown>
        </Tooltip>
      );

      return <Space>{buttons}</Space>;
    },
    width: "60px",
  });

  /**
   *
   * @param {*} id
   */
  const onShowCarePlansHandler = (id) => {
    getSurgicalIntervention(id).then((data) => {
      setSelectedIntervention(data);
      setShowCarePlans(true);
    });
  };

  /**
   *
   */
  const onCloseCarePlansHandler = () => {
    setSelectedIntervention(null);
    setShowCarePlans(false);
  };

  /**
   *
   * @param {*} id
   */
  const onShowForm = (id) => {
    getSurgicalIntervention(id).then((data) => {
      setSelectedIntervention(data);
      setShowForm(true);
    });
  };

  /**
   *
   */
  const onCloseForm = () => {
    setSelectedIntervention(null);
    setShowForm(false);
  };

  /**
   *
   */
  const onEditedHandler = () => {
    onCloseForm();

    props.fetch(pagination, null, sorter);
    if (props.onChange) {
      props.onChange();
    }
  };

  /**
   *
   * @param {*} id
   */
  const onShowRescheduleHandler = (id) => {
    getSurgicalIntervention(id).then((data) => {
      setSelectedIntervention(data);
      setShowReshedule(true);
    });
  };

  /**
   *
   */
  const onCloseRescheduleHandler = () => {
    setSelectedIntervention(null);
    setShowReshedule(false);
  };

  /**
   *
   * @param {*} id
   * @param {*} reason_id
   * @param {*} start_at
   * @param {*} expected_end_at
   */
  const rescheduleHandler = (id, reason_id, start_at, expected_end_at) => {
    start_at = start_at.toISOString(true);
    expected_end_at = expected_end_at.toISOString(true);

    rescheduleSurgicalIntervention(id, reason_id, start_at, expected_end_at);
    onCloseRescheduleHandler();

    props.fetch(pagination, null, sorter);
    if (props.onChange) {
      props.onChange();
    }
  };

  /**
   *
   * @param {*} id
   */
  const onShowOutputTypeHandler = (id) => {
    getSurgicalIntervention(id).then((data) => {
      setSelectedIntervention(data);
      setShowOutputType(true);
    });
  };

  /**
   *
   */
  const closeOutputTypeHandler = () => {
    setSelectedIntervention(null);
    setShowOutputType(false);
  };

  /**
   *
   * @param {*} id
   * @param {*} outputType
   */
  const onOkOutputTypeHandler = (id, outputType) => {
    updateOutputTypeIntervention(id, outputType);
    closeOutputTypeHandler();

    props.fetch(pagination, null, sorter);
    if (props.onChange) {
      props.onChange();
    }
  };

  /**
   *
   * @param {*} id
   */
  const onShowTransfertHandler = (id) => {
    getSurgicalIntervention(id).then((data) => {
      setSelectedIntervention(data);
      setShowTransfer(true);
    });
  };

  /**
   *
   */
  const onCloseTransferHandler = () => {
    setShowTransfer(false);
    setSelectedIntervention(null);
  };

  /**
   *
   * @param {*} id
   */
  const onShowCancelInterventionModal = (id) => {
    getSurgicalIntervention(id).then((data) => {
      setSelectedIntervention(data);
      setShowCancellationModal(true);
    });
  };

  /**
   *
   */
  const onCloseCancellationModal = () => {
    setSelectedIntervention(null);
    setShowCancellationModal(false);
  };

  /**
   *
   */
  const onOkCancellationModal = () => {
    onCloseCancellationModal(false);

    props.fetch(pagination, null, sorter);
    if (props.onCancel) {
      props.onCancel();
    }
  };

  /**
   *
   */
  const onChange = () => {
    props.fetch();
    if (props.onChange) {
      props.onChange();
    }
  }

  return (
    <>
      <TableAntd
        columns={columns}
        rowKey={(record) => record.id}
        dataSource={surgicalInterventions}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          position: ["topRight"]
        }}
        loading={loading}
        onChange={props.fetch}
      />

      <CarePlanList
        visible={showCarePlans}
        surgicalIntervention={selectedIntervention}
        onClose={onCloseCarePlansHandler}
      />

      <SurgicalInterventionForm
        visible={showForm}
        surgicalIntervention={selectedIntervention}
        onCreated={onEditedHandler}
        onClose={onCloseForm}
      />

      <CancellationModal
        visible={showCancellationModal}
        surgicalIntervention={selectedIntervention}
        onOk={onOkCancellationModal}
        onCancel={onCloseCancellationModal}
      />

      <SurgicalInterventionRescheduleForm
        visible={showReshedule}
        surgicalIntervention={selectedIntervention}
        onClose={onCloseRescheduleHandler}
        onUpdate={rescheduleHandler}
      />

      <SurgicalInterventionOutputTypeForm
        visible={showOutputType}
        surgicalIntervention={selectedIntervention}
        onClose={closeOutputTypeHandler}
        onUpdate={onOkOutputTypeHandler}
      />

      <TransferIntervention
        visible={showTransfer}
        surgicalIntervention={selectedIntervention}
        onChange={onChange}
        onClose={onCloseTransferHandler}
      />
    </>
  );
};

export default Table;
